import type { SurplusProfile } from "./surplusComposition";
export enum ProductModule {
  FAMOUS_INTEGRATION = "famous-integration",
  ORDERFUL_EDI = "orderful-edi",
  ORDER_MANAGEMENT = "order-management",
  REBATES = "rebates",
  PROCUREMENT = "procurement",
  INVENTORY_MANAGEMENT = "inventory-management",
  SHIP_AND_DEBIT = "ship-and-debit",
  AP_INVOICING = "ap-invoicing",
  AR_INVOICING = "ar-invoicing",
  ORACLE_EBS_INTEGRATION = "oracle-ebs-integration",
  ORACLE_FUSION_INTEGRATION = "oracle-fusion-integration",
  PRICING = "pricing",
}

// Note: changes to these values can break things in polymorphic relationships. Please be careful.
export enum AIWorkflow {
  EMAIL_SO_INGESTION = "EMAIL_SO_INGESTION",
  PRICE_SHEET_INGESTION = "PRICE_SHEET_INGESTION",
  AP_INVOICE_INGESTION = "AP_INVOICE_INGESTION",
  AR_COLLECTIONS = "AR_COLLECTIONS",
  AR_REMITTANCE_INGESTION = "AR_REMITTANCE_INGESTION",
}

/**
 * Org-facing speed↔quality preference for the AI short-pay recommendation.
 * Semantic on purpose: the backend maps each value to a (model, reasoning
 * effort) pair, so the underlying models can change without touching this
 * org-facing contract. Ordered fastest → highest quality.
 */
export const AR_RECOMMENDATION_SPEED_PREFERENCES = [
  "faster",
  "balanced",
  "highest_quality",
] as const;

export type ArRecommendationSpeedPreference =
  (typeof AR_RECOMMENDATION_SPEED_PREFERENCES)[number];

/**
 * Date basis used to age AR invoices on statement / report surfaces. Mirrors
 * `agingBasisSchema` in the rpc contract (which derives its enum from this
 * list) so there is a single source of truth for the basis values.
 */
export const AR_AGING_BASES = [
  "days_overdue",
  "days_since_invoice",
  "days_since_shipment",
] as const;

export type ArAgingBasis = (typeof AR_AGING_BASES)[number];

/**
 * How the "Customer Statement"-tagged contacts resolved for a customer are
 * split across the To and Cc lines of a statement email.
 *
 * `first_to_rest_cc` is the historical (and default) behavior: the first
 * resolved contact is addressed on To and every other one lands on Cc.
 * `to_all` puts every tagged contact on To and leaves Cc empty.
 *
 * The split is decided client-side in the compose dialog (the server resolver
 * returns a flat ordered email list) and carried to the send endpoint
 * explicitly via `recipientEmails` (To) + `ccRecipientEmails` (Cc).
 */
// Default first: the Settings dropdown renders options in this order.
export const AR_STATEMENT_RECIPIENT_ROLE_MODES = [
  "first_to_rest_cc",
  "to_all",
] as const;

export type ArStatementRecipientRolesMode =
  (typeof AR_STATEMENT_RECIPIENT_ROLE_MODES)[number];

/** Shipped default: today's addressing, so no org is silently re-addressed. */
export const AR_DEFAULT_STATEMENT_RECIPIENT_ROLES: ArStatementRecipientRolesMode =
  "first_to_rest_cc";

export const EMAIL_PROVIDER = {
  SENDGRID: "sendgrid",
  MICROSOFT_GRAPH: "microsoft_graph",
} as const;

export type EmailProvider =
  (typeof EMAIL_PROVIDER)[keyof typeof EMAIL_PROVIDER];

export const WorkflowStepMapping = {
  [AIWorkflow.EMAIL_SO_INGESTION]: ["EXTRACT_SALES_ORDER"],
  [AIWorkflow.PRICE_SHEET_INGESTION]: ["PRICE_SHEET_INGESTION"],
  [AIWorkflow.AP_INVOICE_INGESTION]: ["AP_INVOICE_INGESTION"],
  [AIWorkflow.AR_COLLECTIONS]: ["SHORT_PAY_RECOMMENDATION"],
  [AIWorkflow.AR_REMITTANCE_INGESTION]: ["RESOLVE_INVOICE_REFERENCES"],
} as const satisfies Record<AIWorkflow, string[]>;

export type WorkflowStep =
  (typeof WorkflowStepMapping)[keyof typeof WorkflowStepMapping][number];

export const WorkflowStepValues = Object.values(WorkflowStepMapping).flat();

/**
 * Sources for payment-term precedence when deriving AP invoice due dates.
 * - "invoice": pay terms extracted from the invoice itself
 * - "supplier": pay terms configured on the supplier record
 * - "company": org-level fallback (default net days)
 */
export type PaymentTermsSource = "invoice" | "supplier" | "company";

export const DEFAULT_PAYMENT_TERMS_PRIORITY: readonly PaymentTermsSource[] = [
  "invoice",
  "supplier",
  "company",
];

/**
 * The org's configured payment-terms order, or undefined when unset. Reads
 * `ap.paymentTermsPriority`, falling back to the legacy
 * `ap.paymentScheduling.paymentTermsPriority` location.
 */
export function apPaymentTermsPriority(
  ap: ModuleConfig["ap"] | null | undefined,
): PaymentTermsSource[] | undefined {
  if (ap?.paymentTermsPriority?.length) return ap.paymentTermsPriority;
  if (ap?.paymentScheduling?.paymentTermsPriority?.length) {
    return ap.paymentScheduling.paymentTermsPriority;
  }
  return undefined;
}

export type RegisteredEmailHeaderOption = "to" | "cc";

export type RegisteredEmailDomain = {
  header: RegisteredEmailHeaderOption;
  domain: string;
};

export type AIWorkflowConfig = {
  registeredEmailDomains?: RegisteredEmailDomain[];
};

/**
 * Approval threshold for payment amounts.
 * Used to configure multi-level approval requirements based on payment value.
 *
 * Example thresholds:
 * - { minAmount: 0, requiredApprovals: 1 }      // $0-999 = 1 approval
 * - { minAmount: 1000, requiredApprovals: 2 }   // $1000-9999 = 2 approvals
 * - { minAmount: 10000, requiredApprovals: 3 }  // $10000+ = 3 approvals
 *
 * The highest matching threshold applies (minAmount <= paymentAmount).
 */
export interface ApprovalThreshold {
  /** Payment amounts >= this threshold require this level of approval */
  minAmount: number;
  /** Number of approvals required for this threshold */
  requiredApprovals: number;
}

/**
 * Defaults applied when the org config has no value set for the AR
 * "Send to Internal only" safety toggle. The default is intentionally
 * fail-safe: protection is on, with the Gently corporate domains
 * pre-allow-listed. Customers override by saving explicit values.
 */
export const AR_DEFAULT_INTERNAL_ONLY_ENABLED = true;
export const AR_DEFAULT_INTERNAL_ONLY_DOMAINS: readonly string[] = [
  "gently-ai.com",
  "gentlyhq.com",
];

/**
 * Default applied when the org config has no value for the AR collections
 * "Send to Internal only" toggle (`ar.collections.internalOnlyEnabled`).
 * Fail-safe like AR_DEFAULT_INTERNAL_ONLY_ENABLED: collection-thread emails
 * stay internal-only until an org explicitly opts in to external delivery.
 */
export const AR_DEFAULT_COLLECTIONS_INTERNAL_ONLY_ENABLED = true;

/**
 * Default applied when the org config has no value for
 * `ar.statementContactFooterEnabled`. Defaults to true so the contact footer
 * keeps rendering on statement emails exactly as it did before the toggle
 * existed; orgs that don't want it opt out explicitly.
 */
export const AR_DEFAULT_STATEMENT_CONTACT_FOOTER_ENABLED = true;

/**
 * One entry in the org's collections remediation taxonomy — a known class of
 * discrepancy behind a short pay / over-payment / deduction (a lumper fee, a
 * freight dispute, a promo allowance, …). The AI discrepancy interpreter
 * classifies auto-detected collection threads against this list using the
 * notes the AR team has recorded on the customer.
 */
export interface ArCollectionRemediationClass {
  /**
   * Stable machine key, e.g. "lumper_fee". Referenced from thread metadata,
   * so renaming a key orphans the classification on existing threads —
   * prefer editing the label/guidance over changing keys.
   */
  key: string;
  /** Human label surfaced in thread titles, e.g. "Lumper fee adjustment". */
  label: string;
  /**
   * Optional guidance for the AI classifier: when this class applies, typical
   * amounts or patterns, anything that helps it match customer notes to this
   * class. Plain prose.
   */
  guidance?: string;
}

/**
 * Seeded remediation taxonomy applied when the org config has no
 * `ar.collections.remediationClasses` value. Orgs customize the list in
 * Settings → Modules; saving replaces this default wholesale.
 */
export const AR_DEFAULT_REMEDIATION_CLASSES: readonly ArCollectionRemediationClass[] =
  [
    {
      key: "lumper_fee",
      label: "Lumper fee adjustment",
      guidance:
        "Unloading / lumper service fees deducted from the payment, commonly a flat per-delivery charge.",
    },
    {
      key: "freight_charge",
      label: "Freight or shipping charge dispute",
      guidance:
        "Customer disputes or deducts freight, fuel surcharges, or delivery charges.",
    },
    {
      key: "promo_allowance",
      label: "Promotional or trade allowance",
      guidance:
        "Agreed promotional discounts, rebates, ad allowances, or trade-spend deductions.",
    },
    {
      key: "damaged_or_returned_goods",
      label: "Damaged or returned goods",
      guidance:
        "Deductions for damaged, spoiled, rejected, or returned product.",
    },
    {
      key: "pricing_discrepancy",
      label: "Pricing discrepancy",
      guidance:
        "Invoice price differs from the customer's PO or agreed price list.",
    },
    {
      key: "tax_discrepancy",
      label: "Tax discrepancy",
      guidance:
        "Sales tax or exemption differences between the invoice and the customer's expectation.",
    },
  ];

/**
 * Which thread types a collection action is offered for. The recommendation
 * engine runs on `short_pay` and `over_payment` threads; each action declares
 * the scopes it applies to so the short-pay and over-payment prompts each see
 * only their relevant subset (mirroring the two hand-maintained prompt lists
 * this taxonomy replaces).
 */
export const AR_COLLECTION_ACTION_SCOPES = [
  "short_pay",
  "over_payment",
] as const;
export type ArCollectionActionScope =
  (typeof AR_COLLECTION_ACTION_SCOPES)[number];

/**
 * One configurable collection action the AI recommendation engine may suggest
 * on a thread. This replaces the previously hardcoded `ArCollectionAction`
 * enum + the per-prompt "Allowed Actions" prose: the `description` is rendered
 * straight into the agent prompt, so editing it here (org config) or per
 * customer retunes what the model is told about the action.
 *
 * `id` is a stable machine key echoed back by the model and stored on
 * dismissals, so renaming an id orphans existing dismissals — prefer editing
 * the label/description over changing an id. A small set of ids retain special
 * wiring (`email_sales_person` seeds the sales rep into the draft dialog); every
 * other action is purely advisory.
 */
export interface ArCollectionActionDefinition {
  /** Stable machine key, e.g. "send_payment_reminder". Echoed by the model. */
  id: string;
  /** Human label shown on the recommendation card, e.g. "Send Payment Reminder". */
  label: string;
  /**
   * Prose the agent is given about when this action fits. Rendered verbatim
   * into the prompt's allowed-actions list, so this is the primary lever for
   * steering recommendations.
   */
  description: string;
  /** When false, the agent is not offered this action. */
  enabled: boolean;
  /** Thread types this action is offered for (short pay and/or over-payment). */
  appliesTo: ArCollectionActionScope[];
  /**
   * When true, the recommendation card offers a "Draft email" button for this
   * action (it results in an outbound email). Defaults to false.
   */
  emailable?: boolean;
}

/**
 * Seeded collection-action taxonomy applied when the org config has no
 * `ar.collections.recommendationActions` value. Orgs customize the list in
 * Settings → Modules and per customer on the collections profile; saving
 * replaces the list wholesale. These descriptions were lifted from the former
 * hardcoded short-pay / over-payment prompt lists so behavior is unchanged
 * until an org edits them.
 */
export const AR_DEFAULT_COLLECTION_ACTIONS: readonly ArCollectionActionDefinition[] =
  [
    {
      id: "send_payment_reminder",
      label: "Send Payment Reminder",
      description: "Send an email reminder for an overdue balance.",
      enabled: true,
      appliesTo: ["short_pay"],
      emailable: true,
    },
    {
      id: "schedule_call",
      label: "Schedule Call",
      description:
        "Schedule or place a phone call to the customer — to discuss payment on a short pay, or to confirm intent (prepayment vs. mistake) on an over-payment.",
      enabled: true,
      appliesTo: ["short_pay", "over_payment"],
    },
    {
      id: "email_sales_person",
      label: "Email Sales Person",
      description:
        "Loop in the account's sales rep. Use when the issue is relationship- or contract-driven — a dispute, a missing/mismatched PO or POD on a short pay, or a prepayment/agreed-credit question on an over-payment — that the rep is best placed to resolve.",
      enabled: true,
      appliesTo: ["short_pay", "over_payment"],
      emailable: true,
    },
    {
      id: "adjust_invoice",
      label: "Adjust Invoice",
      description:
        "Issue a billing adjustment or credit on the invoice. Use when the evidence supports a legitimate short pay (an agreed discount, a return, or a pricing/quantity discrepancy between the customer PO and the invoice).",
      enabled: true,
      appliesTo: ["short_pay"],
    },
    {
      id: "apply_credit_to_account",
      label: "Apply Credit to Account",
      description:
        "Park the over-payment as an account credit to net against the customer's next invoice. Use for active, recurring customers — especially when a note or memory-bank arrangement says to hold credits — where a refund would be churn.",
      enabled: true,
      appliesTo: ["over_payment"],
    },
    {
      id: "refund_overpayment",
      label: "Refund Over-payment",
      description:
        "Return the over-paid amount to the customer. Use when there's no standing credit arrangement and no near-term invoice to net against — a one-off or closing account where holding the credit isn't appropriate.",
      enabled: true,
      appliesTo: ["over_payment"],
    },
  ];

/**
 * One "waiting on" reason a record can be parked under via the Set Next Step
 * action. Module-agnostic on purpose: the waiting-on taxonomy is per-org
 * config on whichever product module owns the parked record (AR collection
 * threads today), so the option shape has no module-specific fields.
 *
 * `key` is frozen at create time and is what lands on the owning record's
 * `waiting_on_key` column (e.g. `ar_collection_threads.waiting_on_key`).
 * Renaming `label` is the safe edit; changing `key` orphans every record
 * already parked under it — the same trap documented for
 * ArCollectionActionDefinition.id above.
 *
 * Options are never removed, only disabled: a removed key would leave existing
 * records pointing at nothing. See `resolveWaitingOnLabel`.
 */
export interface WaitingOnOption {
  /** Stable machine key, <= 64 chars. Derived from the label on first create. */
  key: string;
  /** Display label, <= 120 chars. Safe to rename at any time. */
  label: string;
  /**
   * Pre-fills the follow-up date when a reason is picked, so recording a next
   * step stays one interaction. Omit to force an explicit date.
   */
  defaultFollowUpDays?: number;
  /** Disabled options stay resolvable for history but leave the picker. */
  enabled: boolean;
}

/**
 * AR's historical name for {@link WaitingOnOption}, kept as an alias so the
 * collections call sites (and `ar.collections.waitingOnOptions`) keep reading
 * in AR vocabulary. Identical type — not a distinct shape.
 */
export type ArCollectionWaitingOnOption = WaitingOnOption;

/**
 * Seeded waiting-on taxonomy applied when the org config has no
 * `ar.collections.waitingOnOptions` value. Orgs customize the list in
 * Settings → Modules; saving replaces this default wholesale. Each module
 * that adopts the waiting-on machinery brings its own default list of the
 * same shape.
 */
export const AR_DEFAULT_WAITING_ON_OPTIONS: readonly WaitingOnOption[] = [
  {
    key: "customer_response",
    label: "Waiting on customer",
    defaultFollowUpDays: 3,
    enabled: true,
  },
  {
    key: "backup_documents",
    label: "Waiting on backup / POD",
    defaultFollowUpDays: 5,
    enabled: true,
  },
  {
    key: "internal_credit",
    label: "Waiting on credit memo",
    defaultFollowUpDays: 7,
    enabled: true,
  },
  {
    key: "internal_review",
    label: "Waiting on internal review",
    defaultFollowUpDays: 2,
    enabled: true,
  },
  {
    key: "promised_payment",
    label: "Payment promised",
    defaultFollowUpDays: 10,
    enabled: true,
  },
];

/**
 * Seeded waiting-on taxonomy for pricelist update cases, applied when the org
 * config has no `inventoryManagement.pricelistCases.waitingOnOptions` value.
 *
 * Same shape and key style as {@link AR_DEFAULT_WAITING_ON_OPTIONS} (frozen
 * snake_case keys, relabelling is the safe edit) but its own vocabulary: a
 * price change waits on the vendor, on internal review, or on the AS/400 load
 * — never on a customer. Orgs customize the list in Settings → Modules, and
 * saving replaces this default wholesale.
 */
export const IM_PRICELIST_CASE_DEFAULT_WAITING_ON_OPTIONS: readonly WaitingOnOption[] =
  [
    {
      key: "vendor_price_file",
      label: "Waiting on vendor price file",
      defaultFollowUpDays: 5,
      enabled: true,
    },
    {
      key: "vendor_clarification",
      label: "Waiting on vendor clarification",
      defaultFollowUpDays: 3,
      enabled: true,
    },
    {
      key: "internal_review",
      label: "Waiting on internal review",
      defaultFollowUpDays: 2,
      enabled: true,
    },
    {
      key: "as400_load",
      label: "Waiting on AS/400 load",
      defaultFollowUpDays: 3,
      enabled: true,
    },
  ];

/**
 * Resolves a stored waiting-on key to its display label against the org's
 * configured options (including disabled ones — old records must keep
 * rendering). Falls back to the raw key so an orphaned key degrades to
 * something legible instead of a blank cell. Module-agnostic: the caller
 * supplies whichever module's option list the key was frozen against.
 */
export function resolveWaitingOnLabel(
  key: string,
  options: readonly WaitingOnOption[],
): string {
  return options.find((option) => option.key === key)?.label ?? key;
}

/**
 * Derive a stable snake_case machine key from a human label, avoiding
 * already-taken keys via a numeric suffix. Shared by every configurable
 * keyed-list editor (remediation classes, recommendation actions, waiting-on
 * options) so the freeze-key-on-first-create convention has one source.
 */
export function stableKeyFromLabel(
  label: string,
  taken: ReadonlySet<string>,
  fallback: string,
): string {
  const base =
    label
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 60) || fallback;
  let key = base;
  let suffix = 2;
  while (taken.has(key)) {
    key = `${base}_${suffix}`;
    suffix += 1;
  }
  return key;
}

// Type for module configuration objects
/**
 * Replenishment auto-approve rules (GEN-2936). A pending buyer-board line is
 * auto-approved when ALL hold: its item's popularity code at the line's
 * location is in `popularityCodes`; it moved (sold or transferred out) in at
 * least `minMovingMonths` of the last `movementMonths` full months at that
 * location; and the item is stocked in at least `minStockedLocations`
 * locations. Off unless `enabled` is true. The thresholds are a starting
 * proposal pending the customer's sign-off.
 */
export interface ProcurementAutoApproveConfig {
  enabled?: boolean;
  /** Default ["A", "B"]. */
  popularityCodes?: string[];
  /** Default 12. */
  movementMonths?: number;
  /** Default 12: moved in every month of the window. */
  minMovingMonths?: number;
  /** Default 5. */
  minStockedLocations?: number;
}

/**
 * Buyers aim this share above the PO minimum and the prepaid-freight threshold
 * so a cancelled line doesn't drop the order back under (GEN-2931).
 */
export const DEFAULT_THRESHOLD_BUFFER_PCT = 15;
export const MAX_THRESHOLD_BUFFER_PCT = 50;

/** The org's threshold buffer, whole percent; the default when unset or out of range. */
export function procurementThresholdBufferPct(
  procurement: ModuleConfig["procurement"] | null | undefined,
): number {
  const pct = procurement?.thresholdBufferPct;
  return typeof pct === "number" &&
    Number.isInteger(pct) &&
    pct >= 0 &&
    pct <= MAX_THRESHOLD_BUFFER_PCT
    ? pct
    : DEFAULT_THRESHOLD_BUFFER_PCT;
}

export interface ModuleConfig {
  procurement?: {
    leadTimeProfile?: LeadTimeProfile | null;
    surplusProfile?: SurplusProfile;
    autoApprove?: ProcurementAutoApproveConfig;
    /**
     * Whole percent of the PO minimum and of the prepaid-freight threshold the
     * buyer board wants as cushion above each. Unset means
     * DEFAULT_THRESHOLD_BUFFER_PCT.
     */
    thresholdBufferPct?: number;
    /**
     * Product keys the replenishment engine never plans (GEN-2938): note and
     * charge lines on source orders that are not items, such as a
     * hold-until-advised line. Matched exactly after trimming. Read at the
     * start of each engine run, so a change lands on the next run.
     */
    nonItemSkus?: string[];
  };
  emailWorkflows?: {
    [key in AIWorkflow]?: AIWorkflowConfig;
  };
  rebates?: {
    reportedAlgorithm?: "linear-regression" | "conservative";
    conservativeRiskFactor?: number;
    seasonalDampeningFactor?: number;
    enabledStrategies?: ("linear-regression" | "conservative")[];
    /**
     * Per-year overrides for forecasting configuration.
     * Keys are 4-digit year strings (e.g. "2025", "2026").
     * Any field set here takes precedence over the top-level default for that year.
     * If a field is not set for a given year, the top-level value is used.
     *
     * FUTURE DIRECTION:
     * We may eventually support per-agreement overrides (e.g. a `forecastConfig`
     * JSONB column on `rebate_agreements`). The resolution order would become:
     *   agreement.forecastConfig  →  perYear[year]  →  org-level default
     * For multi-agreement views (analytics, reports), per-agreement algorithms
     * would require a "blended" prediction — the aggregation service would need
     * to construct a synthetic prediction set by picking each agreement's
     * configured algorithm's data and summing them. The API response shape
     * (`reportedAlgorithm: string`) would likely become either a blended
     * `'selected'` key or a per-agreement map.
     */
    perYear?: Record<
      string,
      {
        reportedAlgorithm?: "linear-regression" | "conservative";
        conservativeRiskFactor?: number;
        seasonalDampeningFactor?: number;
        enabledStrategies?: ("linear-regression" | "conservative")[];
      }
    >;
    /** Mailbox UPN to send vendor followup emails from (e.g. "rebates@company.com") */
    followupMailboxUpn?: string;
    /** Email template ID used for vendor followup emails */
    followupTemplateId?: string;
    /** CoreEnumOption ID for the contact tag used to find vendor followup recipients */
    followupContactTagOptionId?: string;
    /** Default number of days between followup emails per vendor (default: 30) */
    followupCadenceDays?: number;
    /**
     * CoreEnumOption ID (a "Rebate Rule Tags" option) that marks a rule as
     * belonging to the primary "Redistribution" bucket. ID-based so that
     * renaming the tag does not change categorization. When unset, the write
     * path falls back to a legacy `/redist/i` match on tag names.
     */
    primaryBucketingTagId?: string;
  };
  ap?: {
    invoiceLineItemPurchaseMatching?: string[][]; // Array of arrays of SystemEntity for AND/OR logic
    /** When true, suppress sending remittance emails after payment execution */
    disableRemittanceEmail?: boolean;
    /** Mailbox UPN used to send AP remittance emails through Microsoft Graph */
    remittanceMailboxUpn?: string;
    /** Optional visible From address when sending AP remittances as a group or alias */
    remittanceSenderAddress?: string;
    /** Contact email addresses shown in the remittance email body for supplier inquiries */
    remittanceContactEmails?: string[];
    /** Contact phone number shown in the remittance email body for supplier inquiries */
    remittanceContactPhone?: string;
    // TODO: Add SystemEntity.GOODS_RECEIPT_LINE_ITEM for 3-way matching
    paymentScheduling?: {
      mode: "immediate" | "payRun" | "latestDate";
      cron?: string; // for payRun mode
      immediacy?: "asap" | "latest"; // for payRun mode
      rounding?: "up" | "down" | "nearest"; // for payRun mode
      timezone?: string; // for payRun mode
      daysBeforeDue?: number; // for latestDate mode
      /** @deprecated Read through `apPaymentTermsPriority`; new writes go to `ap.paymentTermsPriority`. */
      paymentTermsPriority?: PaymentTermsSource[];
    };
    /**
     * Ordered precedence for resolving payment terms (invoice due date and
     * scheduled pay date). Unset means DEFAULT_PAYMENT_TERMS_PRIORITY.
     */
    paymentTermsPriority?: PaymentTermsSource[];
    /**
     * Default time of day (HH:mm, 24-hour format) when outgoing payments should be executed.
     * Applied to scheduled payment dates in the organization's timezone.
     * Example: "09:00" for 9 AM, "14:30" for 2:30 PM.
     * If not set, payment dates use midnight (start of day).
     */
    defaultOutgoingPaymentTime?: string;
    /**
     * Approval thresholds for payment amounts.
     * If not configured or empty, no approval is required.
     * Authorization (who can approve) is handled by CASL rules (Action.Execute on Subject.APInvoicePayment).
     */
    approvalThresholds?: ApprovalThreshold[];
  };
  ar?: {
    /**
     * Mailbox UPN used to send AR statements and invoices through Microsoft
     * Graph (e.g. "ar@customer-tenant.com"). When unset, AR emails fall back
     * to the default SendGrid verified sender.
     */
    outboxMailboxUpn?: string;
    /**
     * Optional visible From address when the configured mailbox has permission
     * to send as a group or alias. Only honored when `outboxMailboxUpn` is set.
     */
    outboxSenderAddress?: string;
    /**
     * Default BCC recipients pre-filled on AR statement sends (e.g. a shared
     * AR mailbox that should retain a copy of every statement). Overridable
     * per-send in the compose dialog. Statements are sent from the current
     * user's mailbox by default, so this is how a record copy is retained.
     */
    outboxBccEmails?: string[];
    /**
     * How "Customer Statement"-tagged contacts are split across To and Cc on
     * statement sends. Applies to every compose entry point (payment-reminder
     * task, aging report, customer page). Defaults to
     * AR_DEFAULT_STATEMENT_RECIPIENT_ROLES when unset.
     */
    statementRecipientRoles?: ArStatementRecipientRolesMode;
    /**
     * Contact email addresses appended to AR statement and invoice emails so
     * customers know who to reach with questions. Rendered as mailto links.
     */
    outboxContactEmails?: string[];
    /** Contact phone number appended to AR statement and invoice emails. */
    outboxContactPhone?: string;
    /**
     * When true (the default), statement emails get the contact footer built
     * from `outboxContactEmails` / `outboxContactPhone` appended below the
     * body and signature. Set false to suppress that footer entirely — orgs
     * whose statement template already carries its own sign-off don't want the
     * duplicate. Scoped to statements only: AR invoice emails keep their
     * footer regardless. Defaults to
     * AR_DEFAULT_STATEMENT_CONTACT_FOOTER_ENABLED when unset.
     */
    statementContactFooterEnabled?: boolean;
    /**
     * Logo URL used on AR statement PDFs. When unset, the AR statement
     * generator falls back to the organization-level logo
     * (`OrganizationConfig.logoUrl`).
     */
    logoUrl?: string;
    /**
     * Prefix prepended to the default AR statement PDF attachment filename
     * (e.g. "ACME" → "ACME-statement-CustomerName-2026-05-14.pdf"). Non-
     * alphanumeric characters are stripped during render. Has no effect when
     * the sender supplies a per-send override.
     */
    defaultPdfFilenamePrefix?: string;
    /**
     * Safety gate: when true, AR statement sends are blocked if any
     * To / CC / BCC recipient is outside the configured allow-list of
     * internal email domains. Use to prevent accidental delivery of test
     * data or sensitive aging reports to external recipients. Defaults
     * to AR_DEFAULT_INTERNAL_ONLY_ENABLED when unset on the org config.
     */
    internalOnlyEnabled?: boolean;
    /**
     * Allow-list of email domains treated as internal when
     * `internalOnlyEnabled` is true. Compared case-insensitively against
     * the portion after the @ of each recipient address. Stored as a list;
     * the UI accepts a comma-separated input. Defaults to
     * AR_DEFAULT_INTERNAL_ONLY_DOMAINS when unset on the org config.
     */
    internalOnlyDomains?: string[];
    /**
     * Date basis pre-selected for AR statement / report aging (due date,
     * invoice date, or ship date). When `lockStatementBasis` is true this
     * basis is forced everywhere and the in-UI basis selector is hidden.
     * When unset, surfaces fall back to their own default basis.
     */
    statementDefaultBasis?: ArAgingBasis;
    /**
     * When true, hide the basis selector on AR statement / report surfaces
     * (bulk send dialog, summary aging, custom report builder) and force
     * `statementDefaultBasis`. Stopgap for orgs that only ever use a single
     * basis and find the selector distracting. Requires `statementDefaultBasis`
     * and defaults to off, so surfaces are unchanged until an org opts in.
     */
    lockStatementBasis?: boolean;
    /**
     * Date (YYYY-MM-DD) cash-receipt tracking starts from. Receipts dated
     * before it that nobody has started on (no log task, or one still
     * pending) show as Before Tracking, out of the Open tab. When unset,
     * every receipt is tracked.
     */
    cashReceiptTrackingStartDate?: string;
    /** Check deposits (AR > Check Deposits). */
    checkDeposits?: {
      /**
       * The workflow every file dropped into a check deposit runs through
       * (its trigger: AR_DEPOSIT_FILE_SUBMITTED). Filled in on first use with
       * a copy of the "AR Deposit File Intake" template; point it at another
       * workflow with the same trigger to change how files are read.
       */
      fileIntakeWorkflowId?: string;
    };
    /**
     * Configuration for collections workflows (short-pay auto-detection, etc.).
     */
    collections?: {
      /**
       * Safety gate for collection-thread emails: when true, sends from the
       * collection email draft dialog are blocked if any To / CC / BCC
       * recipient is outside `ar.internalOnlyDomains` (the same allow-list
       * the statement/invoice gate uses). Defaults to
       * AR_DEFAULT_COLLECTIONS_INTERNAL_ONLY_ENABLED when unset.
       */
      internalOnlyEnabled?: boolean;
      /**
       * Master switch for auto-detection of short pays. Opt-in: when unset or
       * false, the detection service is a no-op for this org.
       */
      autoDetectShortPaysEnabled?: boolean;
      /**
       * Minimum residual balance (decimal string, in the invoice currency) to
       * consider an invoice short-paid. Residuals below this floor are ignored
       * so $0.50 rounding pennies don't generate threads. Default: "10".
       */
      shortPayMinimumAmount?: string;
      /**
       * Number of days to wait after the most recent payment application before
       * opening a short_pay thread. Gives the customer a grace window to make a
       * follow-up payment. Default: 3.
       */
      shortPayGraceDays?: number;
      /**
       * Master switch for auto-detection of over-payments (customer paid more
       * than the invoice balance). Opt-in: no-op when unset or false.
       */
      autoDetectOverPaymentsEnabled?: boolean;
      /**
       * Minimum over-payment (decimal string) to open an over_payment thread.
       * Default: "10".
       */
      overPaymentMinimumAmount?: string;
      /**
       * Master switch for auto-detection of deductions claimed on remittances.
       * Opt-in: no-op when unset or false.
       */
      autoDetectDeductionsEnabled?: boolean;
      /**
       * Minimum deduction amount (decimal string) to open a deduction thread.
       * Default: "10".
       */
      deductionMinimumAmount?: string;
      /**
       * Default user (id of an org user) assigned to newly created collection
       * threads. Applied to auto-detected short-pay threads and pre-selected in
       * the manual create-thread form. When unset, new threads start unassigned.
       */
      defaultAssigneeUserId?: string;
      /**
       * AI short-pay recommendation tuning. Exposed as a semantic speed↔quality
       * preference rather than raw model/effort knobs so the org-facing contract
       * stays stable as the underlying model lineup changes; the backend maps
       * each preference to a (model, reasoning effort) pair. Applies to
       * recommendations generated after the change (cached recs are not
       * retroactively regenerated). Default: "faster".
       */
      recommendations?: {
        speedPreference?: ArRecommendationSpeedPreference;
      };
      /**
       * Master switch for AI interpretation of auto-detected discrepancies.
       * When true, thread auto-detection (short pay / over-payment /
       * deduction) classifies each new thread against the org's remediation
       * taxonomy using the notes the AR team recorded on the customer, and
       * titles the thread with the likely cause (e.g. "Likely Lumper fee
       * adjustment — $275.00 short pay on 12345"). Opt-in: no-op when unset
       * or false. Only runs for customers that have comms notes or memory
       * bank notes on file.
       */
      interpretDiscrepanciesEnabled?: boolean;
      /**
       * User-controlled taxonomy of remediation classes the AI interpreter
       * classifies discrepancies against. When unset, falls back to
       * AR_DEFAULT_REMEDIATION_CLASSES. Saving from Settings replaces the
       * list wholesale.
       */
      remediationClasses?: ArCollectionRemediationClass[];
      /**
       * User-controlled catalog of collection actions the AI recommendation
       * engine may suggest on short-pay / over-payment threads. Replaces the
       * former hardcoded action taxonomy: each entry's `description` is rendered
       * into the agent prompt. When unset, falls back to
       * AR_DEFAULT_COLLECTION_ACTIONS. Overridable per customer (see the
       * "ar-collections" entity_module_config document). Saving replaces the
       * list wholesale.
       */
      recommendationActions?: ArCollectionActionDefinition[];
      /**
       * User-controlled list of "waiting on" reasons a rep can park a thread
       * under via the Set Next Step action. Each entry's key is stored on
       * `ar_collection_threads.waiting_on_key`; `defaultFollowUpDays`
       * pre-fills the follow-up date so recording a next step is one
       * interaction. When unset, falls back to AR_DEFAULT_WAITING_ON_OPTIONS.
       * Entries are disabled rather than deleted so stored keys stay
       * resolvable. Saving replaces the list wholesale.
       */
      waitingOnOptions?: ArCollectionWaitingOnOption[];
      /**
       * Free-text, org-wide guidance injected into every collections AI prompt
       * (email drafting + short-pay / over-payment recommendations). The place
       * to set standing tone and basic policy — e.g. "Keep a warm, concise
       * tone; always thank the customer for their business." Per-customer
       * specifics still belong in that customer's comms notes, which layer on
       * top. Empty/unset adds nothing to the prompts.
       */
      agentInstructions?: string;
      /**
       * What to pull out of a backup document an operator downloaded from a
       * customer's vendor portal — the prompt the extraction runs with.
       *
       * Unlike `agentInstructions`, which layers on top of a prompt, this
       * REPLACES the field list in
       * AR_DEFAULT_PORTAL_BACKUP_EXTRACTION_INSTRUCTIONS. Portals differ enough
       * in what they serve — one returns signed PODs, another a claim-detail
       * grid — that a generic list leaves value on the table, and the extracted
       * text is what the recommendation and the document names are built from.
       *
       * AR_PORTAL_BACKUP_EXTRACTION_GUARD is appended regardless, so a rewrite
       * can't drop the "report only what appears, never infer" rule.
       * Empty/unset uses the default.
       */
      portalBackupExtractionInstructions?: string;
    };
    /**
     * Configuration for reconciling Famous-ingested cash receipts (bank-side
     * settlement) against remittance-created payments (customer-side advice).
     */
    cashReceiptReconciliation?: {
      /**
       * Window (in days) around a bank transaction's date to search for a
       * matching remittance-created payment. Default: 2.
       */
      dateWindowDays?: number;
    };
    /**
     * Customer-intelligence platform settings (email-corpus → commsNotes).
     * See docs/ai/customer-intelligence-platform-design.md.
     */
    emailIntelligence?: {
      /**
       * Mailbox UPNs (Microsoft Graph) that have granted consent to be indexed
       * into the customer-intelligence evidence store. The email-corpus index
       * backfill fails closed unless the mailbox it's given is on this list.
       */
      consentedMailboxes?: string[];
    };
  };
  pricing?: {
    // Placeholder for org-level pricing module configuration.
    // Add typed fields here as the module grows.
  };
  orderManagement?: {
    /**
     * Customer IDs that are explicitly enabled for PO ingestion (allowlist).
     * When populated, ONLY these customers will have sales order drafts created.
     * When empty or undefined, falls back to the denylist / all-enabled default.
     */
    enabledCustomerIds?: string[];
    /**
     * @deprecated Use `enabledCustomerIds` instead. Kept for backward compatibility.
     * Customer IDs that are explicitly disabled from PO ingestion (denylist).
     * Ignored when `enabledCustomerIds` is populated.
     */
    disabledCustomerIds?: string[];
    /**
     * Strategy for computing request/delivery dates on sales order drafts.
     * - "inventory-lead-time": Uses inventory status + product lead times
     * - "shipping-method": Uses shipping method (AIR vs OCEAN) to determine lead time
     */
    requestDateStrategy?: "inventory-lead-time" | "shipping-method";
    /**
     * Whether to allow automated order submission via workflows.
     * When false (default), the submit_sales_orders step will skip execution.
     * When true, orders can be auto-submitted without human intervention.
     */
    autoSubmitEnabled?: boolean;
    /**
     * Whether real Oracle EBS submission is enabled for this organization.
     *
     * When `true`: clicking "Approve and Create Sales Order" POSTs the
     * payload to EBS as normal. This is the fully-live mode.
     *
     * When `false` or omitted (**default, safe**): the Approve button still
     * works and the adapter still builds the exact EBS payload and logs it
     * under `[SALES_ORDER_CREATION_PAYLOAD]`, but the HTTP POST is skipped.
     * The order is marked synced with `origSysDocRef = "DRY_RUN:<ref>"` so
     * dry-run submissions are visually and queryably distinct from real ones.
     *
     * This is a **dry-run by default** kill switch: new environments can
     * verify payloads end-to-end without risk of accidental submission,
     * then flip to `true` once EBS wiring is confirmed correct.
     *
     * Does not gate `SalesOrderService.approveSalesOrderDraft` itself — it
     * only affects what the Oracle EBS adapter does on submit. Orgs wanting
     * to hide the Approve button entirely should use RBAC permissions
     * instead of this flag.
     */
    manualApprovalEnabled?: boolean;
    /**
     * Whether to skip description-based product matching during PO ingestion.
     * When false (default), products are matched by both identifier AND description
     * in the CustomerProduct.customerFreeform array.
     * When true, only identifier matching is used, preventing false positives when
     * multiple products share the same description (e.g., "Make-Up, 750 ml.").
     */
    skipDescriptionBasedProductMatching?: boolean;
    /**
     * Suppresses the legacy per-assignee SendGrid email fired when a user is
     * assigned to a new sales order draft (via ingest or customer reassignment).
     *
     * The workflow-based notification system (APPROVAL_NEEDED → resolve_entity_watchers
     * → send_email) is the current, more capable path for order-related notifications,
     * and this legacy path predates it. Orgs should leave this on and only flip
     * to `false` if they still depend on the legacy draft-assignment emails.
     *
     * **Defaults to `true` (legacy emails disabled) when omitted.** An org must
     * explicitly set `false` to keep receiving the legacy SendGrid assignment
     * emails. Disabling the legacy path does NOT affect the watcher assignment
     * (the CoreWatch row is still created); only the email send is skipped.
     */
    legacyDraftAssignmentEmailsDisabled?: boolean;
    /**
     * When true, multiple extracted PO records that share the same
     * purchase_order_number are reconciled into a single record before
     * downstream processing. Useful for organizations whose inbound POs
     * arrive as multi-section documents (cover page + detail page,
     * header sheet + line-item sheet) that the OCR layer sometimes
     * emits as separate records for the same underlying PO.
     *
     * Defaults to `false`. Off-by-default because the merge changes
     * the shape of extracted data (combining line items, picking the
     * richer address) and orgs should opt in explicitly once they
     * have verified the behavior matches their documents.
     */
    mergeSplitPurchaseOrdersEnabled?: boolean;
    /**
     * When true, the org uses the new OMS experience: the OMS v3 pages own
     * the unversioned paths (`/sales-orders`, `/discounts`). When false, those
     * paths redirect to the legacy pages (`/sales-orders-v2`, `/discounts-v1`)
     * — a temporary measure while some tenants remain on the legacy flow.
     * Within the legacy flow it also makes the sales order draft table open
     * the EnhancedSalesOrderDraftModal (richer line-item editing, EBS-aware
     * fields) instead of the legacy SalesOrderDraftModal. Defaults to `false`.
     *
     * This used to be inferred from the presence of the ORACLE_EBS_INTEGRATION
     * module; it is now an explicit per-org config so the enhanced UI can be
     * adopted independently of EBS wiring.
     */
    enhancedSalesOrderUi?: boolean;
    /**
     * Org-wide fallback price list (PriceList.id) used by downstream order
     * pipelines (currently Oracle Fusion sales-order submission) when a
     * customer has no `metadata.price_list_id` override.
     *
     * When omitted, downstream code must handle the missing price list
     * explicitly; this is intentionally a soft default rather than required,
     * since not every org runs the Fusion sync.
     */
    defaultPriceListId?: string;
    /**
     * The org's standard credit terms, in days. A purchase order asking for
     * longer terms is flagged by the review page's extraction check
     * (`terms_policy`) so the reviewer justifies it; pairs with the
     * `order.paymentTermsDays` approval fact for a Finance gate. Omitted →
     * payment terms are not checked.
     */
    standardPaymentTermsDays?: number;
    /**
     * The org's standard shipping terms (Incoterm code or long form, e.g.
     * "EXW" / "Ex Works"). A purchase order naming a different Incoterm is
     * flagged by the `terms_policy` extraction check. Omitted → shipping
     * terms are not checked.
     */
    standardShippingTerms?: string;
    /**
     * Salesperson credited on every order whose customer has no
     * `metadata.salesperson_id` of their own. `salespersonId` is the ERP id
     * the Fusion submission sends as `salesCredits[].SalespersonId`; `name`
     * is what the review page shows. Orgs without a synced Salespersons enum
     * use this as the single option until one exists.
     */
    defaultSalesCredit?: { salespersonId: string; name: string };
    /**
     * Currency of `FROZEN_ITEM_COST` stored under
     * `im_product_variants.origin_metadata`. When set to `"EUR"`, the margin
     * pipeline converts to USD using
     * `oracle-ebs-integration.config.mappings.conversionRates.EUR_USD.rate`.
     * Omitted or `"USD"` means the raw value is already in USD.
     */
    costCurrency?: "USD" | "EUR";
  };
  /** Config stored under `ProductModule.INVENTORY_MANAGEMENT`. */
  inventoryManagement?: {
    /** Pricelist update cases (GEN-2524) — one case per vendor price change. */
    pricelistCases?: {
      /**
       * The reasons a pricelist update case can be parked under when someone
       * sets its next step. Falls back to
       * IM_PRICELIST_CASE_DEFAULT_WAITING_ON_OPTIONS when unset; saving from
       * Settings replaces the list wholesale.
       *
       * Shaped exactly like `ar.collections.waitingOnOptions` and resolved by
       * the same shared machinery (`resolveWaitingOnOptionsFor` /
       * `useWaitingOnOptions`), so the two modules keep their own taxonomies
       * without a second implementation. Options are disabled, never deleted:
       * a removed key would blank the `waiting_on_key` of every case already
       * parked under it.
       */
      waitingOnOptions?: WaitingOnOption[];
    };
  };
  shipAndDebit?: {
    /** Email provider for 844 submissions */
    sd_844_email_provider?: EmailProvider;
    /** Mailbox UPN for Microsoft Graph email sending (required when provider is microsoft_graph) */
    sd_844_email_mailbox_upn?: string;
    /**
     * Who to notify when AS400 REJECTS an 844 batch file (it renames the
     * uploaded GEN844 with an `OUT` prefix and writes the reasons into it — see
     * `as400ErrorDetection`). Editable on the Company Locations admin page, so
     * adding or removing a person never needs a deploy.
     *
     * When empty, the notification goes to the S&D team inbox
     * (`SD_844_EMAIL_RECIPIENT`, else shipanddebit@rexel.ca) — the alert is
     * never silently dropped for want of configuration.
     */
    sd_844_error_notification_emails?: string[];
    /**
     * Hard cap on how many emails the 849 Review queue puts in front of a
     * reviewer at once.
     *
     * A cap rather than a pure threshold: a threshold can be overrun by a bad
     * week, and a queue with no visible bottom is a queue nobody opens. Omitted
     * → `REVIEW_QUEUE_DEFAULT_CAP`. Clamped server-side; see
     * `resolveReviewQueueCap`.
     */
    sd_849_review_queue_cap?: number;
    /**
     * The fraction of CONFIDENTLY-classified 849 emails drawn into the review
     * queue as a blind audit sample (#7170). Why the sample exists is in
     * `classify849/auditSample.ts`.
     *
     * Omitted or zero → OFF, which is today's behaviour for every org; clamped
     * server-side, see `resolveAuditSampleRate`.
     *
     * A rate, not a count: roughly ten a week is the target, so the value is set
     * against the org's own classification volume.
     */
    sd_849_audit_sample_rate?: number;
    /**
     * RETIRED (#7657): read by nothing. It chose between the thread and the claim
     * as the grouping key of the per-email reconcile verb (#7177), which is gone —
     * every email 849 is now one record per 844 claim (ADR 0005). Kept only so
     * stored org configs still parse; remove it in a later release.
     */
    sd_849_group_by_claim?: boolean;
    /**
     * Org-wide JE template defaults for the GL posting Excel (Download GL
     * Posting / Post All Staged). These fill the JE Template sheet's header
     * fields. Applied to every division; `divisionName` still comes from
     * `sd_company_locations`. When a field is blank/unset, the posting builder
     * falls back to its per-division hardcoded map, then to built-in defaults.
     */
    sd_gl_je_template?: {
      /** TRANS TYPE cell (JE type code, e.g. "JE") */
      trans_type?: string;
      /** User ID cell (AS400 user, e.g. "USCHARRO") */
      user_id?: string;
      /** User email cell (semicolon-separated recipients allowed) — org-wide fallback */
      user_email?: string;
      /**
       * Per-division User email overrides, keyed by division key
       * (companyCode + divisionCode, e.g. "W+", "QO"). When a division has a
       * non-empty entry it wins over `user_email`; otherwise `user_email` is the
       * org-wide fallback. Semicolon-separated recipients allowed per division.
       */
      user_email_by_division?: Record<string, string>;
      /** Source Code cell (e.g. "SC") */
      source_code?: string;
    };
    /**
     * Per-division shipping province for NO-TAX VDMs (suppliers flagged
     * `sd_vdm_no_tax`). Those suppliers get ONE VDM per division with tax off,
     * stamped with this province instead of the per-transaction tax province.
     * Keyed by division key (companyCode + divisionCode, e.g. "W+", "QE").
     * A division with no entry excludes its no-tax transactions from the VDM
     * preview with a "province not configured" warning.
     */
    sd_vdm_no_tax_province_by_division?: Record<string, string>;
  };
  oracleEbs?: {
    /** URL of the Oracle Query Server (e.g., http://localhost:3030) */
    queryServerUrl?: string;
    /** EBS organization name for the Order Status API (e.g., "USA", "DEMEERN") */
    ebsOrgName?: string;
    /** Default organization code when no planner mapping is found */
    defaultOrgCode?: string;
    /** Default order type when no planner mapping is found */
    defaultOrderType?: { id: number; name: string };
    /** Default price list name for price lookups (e.g., "EEME&A DISTRIBUTOR") */
    defaultPriceListName?: string;
    /** Default inventory organization ID when no planner mapping is found */
    defaultInventoryOrgId?: number;
    /**
     * Oracle `fob_point` sent on every EBS sales order submission (e.g.
     * "Shipping Point"). Sent as "" when unset.
     */
    fobPoint?: string;
    /**
     * Hardcoded promo code sent on every Oracle EBS sales order submission.
     * Varies by organization. Omitted from the payload when unset.
     */
    promoCode?: string;
    /**
     * Hardcoded sales channel code sent on every Oracle EBS sales order
     * submission. Varies by organization. Omitted from the payload when unset.
     */
    salesChannelCode?: string;
  };
  oracleFusion?: {
    /** Business Unit ID for sales orders (e.g., 300000002262068) */
    businessUnitId?: number;
    /** Order type code (e.g., "SG IB") */
    transactionTypeCode?: string;
    /** Fulfillment organization ID (e.g., 300000002250260) */
    fulfillmentOrganizationId?: number;
    /** Payment terms code (e.g., 4 = "30 Net") */
    paymentTermsCode?: number;
    /** Freight terms code (e.g., "DAP") */
    freightTermsCode?: string;
    /**
     * Carrier to put on the order when the PO names a forwarder Fusion does
     * not list as a carrier (e.g. "See Remarks"); the forwarder itself goes
     * in Shipping Instructions. Unset: no carrier is picked for such POs.
     */
    unlistedCarrierName?: string;
    /** Default currency code (e.g., "SGD") */
    defaultCurrencyCode?: string;
    /** Source transaction system identifier (e.g., "OPS") */
    sourceTransactionSystem?: string;
    /** Default line type code (e.g., "ORA_BUY") */
    defaultLineTypeCode?: string;
  };
  /**
   * Generic customer data mappings exposed via the CRUD UI.
   *
   * Each key is a map name (e.g. "plannerMap", "shippingMap").
   * Each value is a Record<entryKey, entryValue> where entryValue is an
   * arbitrary object whose shape is inferred at runtime by the UI.
   *
   * Business logic consumers should cast the entries to their expected shape.
   */
  mappings?: Record<string, Record<string, unknown>>;
  /**
   * Display metadata for each mapping in `mappings`.
   *
   * Keys correspond to the map names in `mappings`.
   * This is purely a UI concern — backend consumers ignore it.
   */
  mappingsMeta?: Record<
    string,
    {
      /** Custom display name for the mapping tab (e.g. "Planner Codes") */
      displayName?: string;
      /**
       * Label for the map-key column.
       * - A string shows the key column with that label.
       * - `null` hides the key column from the UI entirely.
       */
      keyLabel?: string | null;
      /**
       * Explicit column display order (dot-notation keys).
       * Columns not listed are appended at the end.
       * Without this, column order is unpredictable (jsonb doesn't preserve key order).
       */
      columnOrder?: string[];
    }
  >;
}

/**
 * Default `org_module_config.config` seeded for the Order Management module when
 * an organization is provisioned with it enabled (e.g. tenant-admin org creation).
 * Gives new orgs sensible OM behavior out of the box without copying the source
 * org's per-module config (which carries org-specific email-domain routing).
 */
export const DEFAULT_ORDER_MANAGEMENT_MODULE_CONFIG: ModuleConfig = {
  orderManagement: {
    autoSubmitEnabled: false,
    costCurrency: "USD",
    enabledCustomerIds: [],
    requestDateStrategy: "shipping-method",
    enhancedSalesOrderUi: true,
    manualApprovalEnabled: true,
    skipDescriptionBasedProductMatching: true,
  },
};

// Type for organization module configuration
export interface OrganizationModuleConfig {
  id: string;
  organizationId: string;
  module: ProductModule;
  enabled: boolean;
  config: ModuleConfig;
  createdAt: Date;
  updatedAt: Date;
}
import type { LeadTimeProfile } from "./leadTimeComposition";
